<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-device-energy">
    <nx-report-header title="设备能耗统计">
      <div class="de-header-actions">
        <span class="de-refresh-time">数据刷新 {{ displayText(report.refreshTime || report.generatedTime) }}</span>
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <el-form :inline="true" class="de-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="统计周期">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="width: 260px"
        />
      </el-form-item>
      <el-form-item label="设备名称">
        <el-input v-model="filters.deviceName" clearable placeholder="请输入设备名称" style="width: 180px" />
      </el-form-item>
      <el-form-item label="设备分类">
        <el-input v-model="filters.category" clearable placeholder="全部分类" style="width: 150px" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="loadData">查询</el-button>
        <el-button icon="el-icon-refresh-left" @click="resetFilters">重置</el-button>
      </el-form-item>
    </el-form>

    <div class="de-kpi-row">
      <div class="de-kpi-card energy">
        <div class="de-kpi-label">总能耗</div>
        <div class="de-kpi-value">{{ fmtNum(summary.totalEnergyKwh) }}<span>kWh</span></div>
        <div class="de-kpi-sub">额定功率 × 运行时长</div>
      </div>
      <div class="de-kpi-card runtime">
        <div class="de-kpi-label">总运行时长</div>
        <div class="de-kpi-value">{{ fmtNum(summary.totalRuntimeHours) }}<span>h</span></div>
        <div class="de-kpi-sub">来自 OPC 运行区间统计</div>
      </div>
      <div class="de-kpi-card count">
        <div class="de-kpi-label">统计设备数</div>
        <div class="de-kpi-value">{{ fmtInt(summary.deviceCount) }}<span>台</span></div>
        <div class="de-kpi-sub">有功率或运行记录的设备</div>
      </div>
      <div class="de-kpi-card load">
        <div class="de-kpi-label">平均负载功率</div>
        <div class="de-kpi-value">{{ fmtNum(avgPower) }}<span>kW</span></div>
        <div class="de-kpi-sub">总能耗 / 总运行时长</div>
      </div>
    </div>

    <div class="de-chart-grid">
      <div class="de-section">
        <div class="de-section-head">
          <span>能耗趋势</span>
          <small>按日期汇总设备运行能耗</small>
        </div>
        <div ref="trendChart" class="de-chart-box" />
      </div>
      <div class="de-section">
        <div class="de-section-head">
          <span>能耗排行 TOP10</span>
          <small>按设备能耗降序</small>
        </div>
        <div ref="rankChart" class="de-chart-box" />
      </div>
    </div>

    <div class="de-chart-grid de-chart-grid--side">
      <div class="de-section">
        <div class="de-section-head">
          <span>分类占比</span>
          <small>按设备分类统计能耗</small>
        </div>
        <div ref="categoryChart" class="de-chart-box de-chart-box--sm" />
      </div>
      <div class="de-section">
        <div class="de-section-head">
          <span>计算口径</span>
          <small>设备能耗 = 额定功率(kW) × 运行时长(h)</small>
        </div>
        <div class="de-rule-list">
          <div>
            <label>额定功率</label>
            <span>设备台账字段 ratedPower / rated_power</span>
          </div>
          <div>
            <label>运行时长</label>
            <span>OPC 点位历史值表按运行状态区间累计</span>
          </div>
          <div>
            <label>统计范围</label>
            <span>{{ dateRangeText }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="de-section">
      <div class="de-section-head">
        <span>设备能耗明细</span>
        <small>共 {{ tableRows.length }} 台</small>
      </div>
      <el-table :data="pagedRows" border stripe size="small" empty-text="暂无设备能耗数据">
        <el-table-column type="index" label="序号" width="56" align="center" />
        <el-table-column prop="deviceName" label="设备名称" min-width="170" show-overflow-tooltip />
        <el-table-column prop="category" label="设备分类" width="110" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ displayText(row.category) }}</template>
        </el-table-column>
        <el-table-column prop="areaName" label="区域/位置" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ displayText(row.areaName || row.deviceLocation) }}</template>
        </el-table-column>
        <el-table-column label="额定功率(kW)" width="125" align="right">
          <template slot-scope="{ row }">{{ fmtNum(row.ratedPower) }}</template>
        </el-table-column>
        <el-table-column label="运行时长(h)" width="120" align="right">
          <template slot-scope="{ row }">{{ fmtNum(row.runtimeHours) }}</template>
        </el-table-column>
        <el-table-column label="能耗(kWh)" width="125" align="right">
          <template slot-scope="{ row }">
            <strong class="de-energy-cell">{{ fmtNum(row.energyKwh) }}</strong>
          </template>
        </el-table-column>
        <el-table-column label="运行占比" width="110" align="right">
          <template slot-scope="{ row }">{{ fmtPercent(row.runtimeRatio) }}</template>
        </el-table-column>
        <el-table-column label="最后运行时间" width="170">
          <template slot-scope="{ row }">{{ displayText(row.lastRunTime || row.lastCollectTime) }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="tableRows.length > pageSize"
        class="de-pagination"
        :current-page.sync="page"
        :page-size="pageSize"
        layout="total, prev, pager, next"
        :total="tableRows.length"
      />
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchDeviceEnergy } from './api'

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0
  const n = Number(value)
  return Number.isNaN(n) ? 0 : n
}

function firstValue(obj, keys, fallback) {
  if (!obj) return fallback
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') return obj[key]
  }
  return fallback
}

export default {
  name: 'KanbanDeviceEnergy',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      report: {},
      dateRange: [],
      filters: {
        deviceName: '',
        category: ''
      },
      page: 1,
      pageSize: 15,
      chartTrend: null,
      chartRank: null,
      chartCategory: null
    }
  },
  computed: {
    normalizedRows() {
      const rows = firstValue(this.report, ['deviceRows', 'rows', 'list', 'records', 'detailList'], [])
      if (!Array.isArray(rows)) return []
      return rows.map((row) => {
        const ratedPower = toNumber(firstValue(row, ['ratedPower', 'rated_power', 'power', 'm_rated_power'], 0))
        const runtimeHours = toNumber(firstValue(row, ['runtimeHours', 'runtime_hours', 'runHours', 'runningHours'], 0))
        const energyKwh = toNumber(firstValue(row, ['energyKwh', 'energy_kwh', 'energy', 'totalEnergyKwh'], ratedPower * runtimeHours))
        return {
          ...row,
          deviceName: firstValue(row, ['deviceName', 'device_name', 'name', 'm_name'], '--'),
          category: firstValue(row, ['category', 'deviceType', 'device_type', 'm_category'], ''),
          areaName: firstValue(row, ['areaName', 'area_name', 'area', 'deptName'], ''),
          deviceLocation: firstValue(row, ['deviceLocation', 'device_location', 'location'], ''),
          ratedPower,
          runtimeHours,
          energyKwh,
          runtimeRatio: firstValue(row, ['runtimeRatio', 'runtime_ratio', 'runRatio'], null),
          lastRunTime: firstValue(row, ['lastRunTime', 'last_run_time', 'lastRunAt'], null),
          lastCollectTime: firstValue(row, ['lastCollectTime', 'last_collect_time'], null)
        }
      })
    },
    tableRows() {
      const name = (this.filters.deviceName || '').trim()
      const category = (this.filters.category || '').trim()
      return this.normalizedRows
        .filter((row) => {
          if (name && String(row.deviceName || '').indexOf(name) === -1) return false
          if (category && String(row.category || '').indexOf(category) === -1) return false
          return true
        })
        .sort((a, b) => toNumber(b.energyKwh) - toNumber(a.energyKwh))
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize
      return this.tableRows.slice(start, start + this.pageSize)
    },
    summary() {
      const s = firstValue(this.report, ['summary', 'kpi'], {}) || {}
      const totalEnergyKwh = toNumber(firstValue(s, ['totalEnergyKwh', 'total_energy_kwh', 'energyKwh'], null)) ||
        this.tableRows.reduce((sum, row) => sum + toNumber(row.energyKwh), 0)
      const totalRuntimeHours = toNumber(firstValue(s, ['totalRuntimeHours', 'total_runtime_hours', 'runtimeHours'], null)) ||
        this.tableRows.reduce((sum, row) => sum + toNumber(row.runtimeHours), 0)
      const deviceCount = toNumber(firstValue(s, ['deviceCount', 'device_count', 'count'], null)) || this.tableRows.length
      return { totalEnergyKwh, totalRuntimeHours, deviceCount }
    },
    avgPower() {
      if (!this.summary.totalRuntimeHours) return 0
      return this.summary.totalEnergyKwh / this.summary.totalRuntimeHours
    },
    trendRows() {
      const rows = firstValue(this.report, ['trend', 'trendRows', 'dailyTrend', 'dateRows'], [])
      if (Array.isArray(rows) && rows.length) {
        return rows.map((row) => ({
          date: firstValue(row, ['date', 'statDate', 'stat_date', 'day'], ''),
          energyKwh: toNumber(firstValue(row, ['energyKwh', 'energy_kwh', 'energy'], 0)),
          runtimeHours: toNumber(firstValue(row, ['runtimeHours', 'runtime_hours'], 0))
        }))
      }
      return []
    },
    categoryRows() {
      const rows = firstValue(this.report, ['categoryStats', 'categoryRows', 'typeStats'], [])
      if (Array.isArray(rows) && rows.length) {
        return rows.map((row) => ({
          name: firstValue(row, ['category', 'deviceType', 'device_type', 'name'], '未分类'),
          value: toNumber(firstValue(row, ['energyKwh', 'energy_kwh', 'energy', 'value'], 0))
        }))
      }
      const map = {}
      this.tableRows.forEach((row) => {
        const key = row.category || '未分类'
        map[key] = (map[key] || 0) + toNumber(row.energyKwh)
      })
      return Object.keys(map).map((key) => ({ name: key, value: map[key] }))
    },
    dateRangeText() {
      if (!this.dateRange || this.dateRange.length !== 2) return '默认按后端统计周期返回'
      return `${this.dateRange[0]} 至 ${this.dateRange[1]}`
    }
  },
  watch: {
    tableRows() {
      this.page = 1
      this.$nextTick(() => this.renderCharts())
    }
  },
  mounted() {
    this.initDateRange()
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    ;[this.chartTrend, this.chartRank, this.chartCategory].forEach((chart) => {
      if (chart) chart.dispose()
    })
  },
  methods: {
    initDateRange() {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 6)
      this.dateRange = [this.formatDate(start), this.formatDate(end)]
    },
    formatDate(date) {
      const pad = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    },
    displayText(value) {
      return value === null || value === undefined || value === '' ? '--' : value
    },
    fmtNum(value) {
      const n = toNumber(value)
      return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    fmtInt(value) {
      return Math.round(toNumber(value)).toLocaleString('zh-CN')
    },
    fmtPercent(value) {
      if (value === null || value === undefined || value === '') return '--'
      const n = toNumber(value)
      return `${n > 1 ? n.toFixed(2) : (n * 100).toFixed(2)}%`
    },
    buildQuery() {
      const params = {
        deviceName: this.filters.deviceName || undefined,
        category: this.filters.category || undefined
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0]
        params.endDate = this.dateRange[1]
      }
      return params
    },
    async loadData() {
      this.loading = true
      try {
        this.report = await fetchDeviceEnergy(this.buildQuery()) || {}
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = { deviceName: '', category: '' }
      this.initDateRange()
      this.loadData()
    },
    onResize() {
      [this.chartTrend, this.chartRank, this.chartCategory].forEach((chart) => chart && chart.resize())
    },
    ensureChart(refName, chartName) {
      const el = this.$refs[refName]
      if (!el) return null
      if (!this[chartName]) this[chartName] = echarts.init(el)
      return this[chartName]
    },
    renderCharts() {
      this.renderTrendChart()
      this.renderRankChart()
      this.renderCategoryChart()
    },
    renderTrendChart() {
      const chart = this.ensureChart('trendChart', 'chartTrend')
      if (!chart) return
      const rows = this.trendRows
      chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { top: 0, right: 8, data: ['能耗(kWh)', '运行时长(h)'] },
        grid: { left: 48, right: 24, top: 42, bottom: 32 },
        xAxis: { type: 'category', data: rows.map(row => row.date), axisLabel: { color: '#64748b' } },
        yAxis: [
          { type: 'value', name: 'kWh', axisLabel: { color: '#64748b' } },
          { type: 'value', name: 'h', axisLabel: { color: '#64748b' } }
        ],
        series: [
          {
            name: '能耗(kWh)',
            type: 'bar',
            data: rows.map(row => row.energyKwh),
            itemStyle: { color: '#2563eb' }
          },
          {
            name: '运行时长(h)',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            data: rows.map(row => row.runtimeHours),
            lineStyle: { color: '#16a34a', width: 2 },
            itemStyle: { color: '#16a34a' }
          }
        ]
      })
    },
    renderRankChart() {
      const chart = this.ensureChart('rankChart', 'chartRank')
      if (!chart) return
      const rows = this.tableRows.slice(0, 10).reverse()
      chart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 100, right: 24, top: 18, bottom: 24 },
        xAxis: { type: 'value', name: 'kWh', axisLabel: { color: '#64748b' } },
        yAxis: {
          type: 'category',
          data: rows.map(row => row.deviceName),
          axisLabel: { color: '#475569', width: 90, overflow: 'truncate' }
        },
        series: [
          {
            type: 'bar',
            data: rows.map(row => row.energyKwh),
            barWidth: 14,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#38bdf8' },
                { offset: 1, color: '#2563eb' }
              ])
            }
          }
        ]
      })
    },
    renderCategoryChart() {
      const chart = this.ensureChart('categoryChart', 'chartCategory')
      if (!chart) return
      const rows = this.categoryRows.filter(row => row.value > 0)
      chart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c} kWh ({d}%)' },
        legend: { bottom: 0, type: 'scroll' },
        series: [
          {
            type: 'pie',
            radius: ['38%', '62%'],
            center: ['50%', '44%'],
            data: rows.length ? rows : [{ name: '暂无数据', value: 1, itemStyle: { color: '#e2e8f0' } }],
            label: { formatter: '{b}\n{d}%', fontSize: 11 }
          }
        ]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
